import React from 'react';
import classNames from 'classnames';
import { useObserver } from 'mobx-react';
import { useTheme } from '@mui/material/styles';

import { useManagedDevicesUIStore } from '@core/useStores';
import {
    PAIRING_STATUS_ACTIVE,
    PAIRING_STATUS_DENIED,
    PAIRING_STATUS_EXPIRED,
    PAIRING_STATUS_REVOKED,
    PAIRING_STATUS_SIGNED_OUT,
    PAIRING_STATUS_SUPERSEDED,
    PairedDeviceSummary,
} from 'AuthPart/DevicePairing/services';

import { useStyles } from './ManagedDevicesPage.styles';
import { messages, pairingStatusLabel } from 'AuthPart/DevicePairing/devicePairing.messages';
import { ThemeProps } from '@styles/theme';

type StatusEntry = {
    status: string;
    count: (summary: PairedDeviceSummary) => number;
    // 'good' and 'critical' are reserved status meanings, not decoration. Everything else stays
    // neutral so the two that matter keep their weight.
    tone?: 'good' | 'critical';
};

const STATUSES: StatusEntry[] = [
    { status: PAIRING_STATUS_ACTIVE, count: s => s.active, tone: 'good' },
    { status: PAIRING_STATUS_REVOKED, count: s => s.revoked, tone: 'critical' },
    { status: PAIRING_STATUS_SIGNED_OUT, count: s => s.signedOut },
    { status: PAIRING_STATUS_SUPERSEDED, count: s => s.superseded },
    { status: PAIRING_STATUS_EXPIRED, count: s => s.expired },
    { status: PAIRING_STATUS_DENIED, count: s => s.denied },
];

/**
 * The states a pairing can be in, each with how many there are, doubling as the filter.
 *
 * Counts and filter are one control on purpose: a count you cannot act on invites the question
 * "which three are revoked?" and then leaves you to hunt for them.
 *
 * Colour never carries the meaning on its own — every entry has its label and its number, and the
 * two states worth a tone (live, revoked) get a marker beside the text rather than coloured text.
 */
export const PairingStatusFilter: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const managedDevicesUIStore = useManagedDevicesUIStore();

    return useObserver(() => {
        const { summary, statuses } = managedDevicesUIStore;

        if (!summary) {
            return null;
        }

        return (
            <section className={styles.summary} data-testid="pairing-status-filter">
                <div className={styles.summaryHeader}>
                    <span className={styles.summaryTitle}>{messages['managedDevices.summary.title']}</span>
                    <span className={styles.summaryHint}>{messages['managedDevices.summary.hint']}</span>
                </div>

                <div className={styles.summaryRow}>
                    <div className={styles.summaryTotal}>
                        <span className={styles.summaryCount}>{summary.total}</span>
                        <span className={styles.summaryLabel}>{messages['managedDevices.summary.total']}</span>
                    </div>

                    {
                        STATUSES.map(({ status, count, tone }) => {
                            const selected = statuses.includes(status);

                            return (
                                <button
                                    key={status}
                                    type="button"
                                    aria-pressed={selected}
                                    className={classNames(styles.summaryTile, {
                                        [styles.summaryTileSelected]: selected,
                                    })}
                                    onClick={() => managedDevicesUIStore.toggleStatus(status)}
                                >
                                    <span className={styles.summaryCount}>{count(summary)}</span>
                                    <span className={styles.summaryLabel}>
                                        {
                                            tone && (
                                                <span
                                                    className={classNames(styles.marker, {
                                                        [styles.markerGood]: tone === 'good',
                                                        [styles.markerCritical]: tone === 'critical',
                                                    })}
                                                />
                                            )
                                        }
                                        {pairingStatusLabel(status)}
                                    </span>
                                </button>
                            );
                        })
                    }
                </div>
            </section>
        );
    });
}
